import { CSSProperties } from 'react';
import { useField } from 'informed';
import { useReduxState } from 'hooks';

interface Props {
  ariaLabel?: string;
  darkMode?: boolean;
  disabled?: boolean;
  field: string;
  id: string;
  label?: string;
  max: number;
  min: number;
  onChange?: (value: number) => void;
  showValue?: boolean;
  stepValue?: number;
  style?: CSSProperties;
  valueUnit?: string;
}

const Range = (props: Props) => {
  const { theme } = useReduxState();

  // @ts-ignore
  const { fieldApi, fieldState, render, userProps } = useField<number>({
    ...props
  });

  const {
    ariaLabel,
    darkMode,
    disabled,
    label,
    max,
    min,
    onChange,
    showValue = true,
    stepValue,
    style,
    valueUnit
  } = userProps;

  let thumbColor = theme.textColorLighter;
  let thumbOutlineColor = 'rgba(0, 0, 0, 0.15)';
  let trackColor = 'rgba(0, 0, 0, 0.1)';
  let trackFillColor = 'rgba(0, 0, 0, 0.3)';
  let labelColor = theme.textColorLight;
  if (darkMode) {
    thumbColor = '#eee';
    thumbOutlineColor = 'rgba(255, 255, 255, 0.1)';
    trackColor = 'rgba(255, 255, 255, 0.1)';
    trackFillColor = 'rgba(255, 255, 255, 0.4)';
    labelColor = '#fff';
  }

  const thumbSize = 16;
  const fillPercentage = ((fieldState.value - min) / (max - min)) * 100;
  const trackBackground = `linear-gradient(to right, ${trackFillColor} ${fillPercentage}%, ${trackColor} ${fillPercentage}%, ${trackColor} 100%)`;
  const trackHeight = 8;

  return render(
    <div className="range" style={style}>
      <div className="label">
        {label ? <div className="label-text">{label}</div> : null}
        {showValue ? (
          <div className="label-value">{`${fieldState.value}${valueUnit}`}</div>
        ) : null}
      </div>
      <input
        className="range-input"
        disabled={disabled}
        max={max}
        min={min}
        onChange={(ev) => {
          const value = parseInt(ev.target.value, 10);
          fieldApi.setValue(value);
          if (onChange) onChange(value);
        }}
        step={stepValue}
        type="range"
        value={fieldState.value}
        {...(ariaLabel
          ? {
              'aria-label': ariaLabel
            }
          : {})}
      />

      <style jsx>{`
        .label {
          color: ${labelColor};
          display: flex;
          font-size: 1.5rem;
          font-weight: 400;
          line-height: 1;
          opacity: ${disabled ? 0.5 : 1};
        }
        .label-text {
          flex-grow: 1;
          margin-right: 20px;
        }
        .range-input {
          background-color: transparent;
          margin: ${thumbSize / 2}px 0;
          width: 100%;
          -webkit-appearance: none;
        }
        .range-input:focus {
          outline: none;
        }
        .range-input:disabled {
          opacity: 0.5;
        }
        .range-input::-webkit-slider-runnable-track {
          background: ${trackBackground};
          border: 0;
          border-radius: ${trackHeight}px;
          cursor: pointer;
          height: ${trackHeight}px;
          width: 100%;
        }
        .range-input:focus::-webkit-slider-runnable-track {
          background: ${trackBackground};
        }
        .range-input:disabled::-webkit-slider-runnable-track {
          cursor: default;
        }
        .range-input::-webkit-slider-thumb {
          background: ${thumbColor};
          border: 0;
          border-radius: ${thumbSize}px;
          cursor: pointer;
          height: ${thumbSize}px;
          margin-top: -4px;
          width: ${thumbSize}px;
          -webkit-appearance: none;
        }
        .range-input:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 5px ${thumbOutlineColor};
        }
        .range-input:disabled::-webkit-slider-thumb {
          cursor: default;
        }
        .range-input::-moz-range-track {
          background: ${trackBackground};
          border: 0;
          border-radius: ${trackHeight}px;
          cursor: pointer;
          height: ${trackHeight}px;
          width: 100%;
        }
        .range-input:disabled::-moz-range-track {
          cursor: default;
        }
        .range-input::-moz-range-thumb {
          background: ${thumbColor};
          border: 0;
          border-radius: ${thumbSize}px;
          cursor: pointer;
          height: ${thumbSize}px;
          width: ${thumbSize}px;
        }
        .range-input:focus::-moz-range-thumb {
          box-shadow: 0 0 0 1px ${thumbOutlineColor};
        }
        .range-input:disabled::-moz-range-thumb {
          cursor: default;
        }
        .range-input::-moz-focus-outer {
          border: 0;
        }
        .range-input::-ms-track {
          background: transparent;
          border-color: transparent;
          border-width: 2px 0 0 0;
          color: transparent;
          cursor: pointer;
          height: ${trackHeight}px;
          width: 100%;
        }
        .range-input::-ms-fill-lower {
          background: ${trackFillColor};
          border: 0;
        }
        .range-input::-ms-fill-upper {
          background: ${trackColor};
          border: 0;
        }
        .range-input::-ms-thumb {
          background: ${thumbColor};
          border: 0;
          border-radius: ${thumbSize}px;
          cursor: pointer;
          height: ${thumbSize}px;
          width: ${thumbSize}px;
        }
        .range-input:focus::-ms-fill-lower {
          background: ${trackFillColor};
        }
        .range-input:focus::-ms-fill-upper {
          background: ${trackColor};
        }
      `}</style>
    </div>
  );
};

export default Range;
